import { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, LayoutGrid, LayoutList, Mail, MapPin, Briefcase, Building2, X, Eye, Loader2 } from 'lucide-react';
// Resources page with card and list views
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useScheduler, mapApiResourceToResource } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useDataMode } from '@/contexts/DataModeContext';
import { resourcesApi } from '@/services/api';
import { ResourceStatus, WorkingHoursProfile } from '@/types';
import { calculateResourceUtilization } from '@/lib/utils/calendar';
import { DataModeToggle } from '@/components/DataModeToggle';
import { ResourceDetailsModal } from '@/components/resources/ResourceDetailsModal';
import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter, 
  startOfYear, 
  endOfYear,
  addDays
} from 'date-fns';

type UtilizationPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
type ViewMode = 'grid' | 'card';

export function Resources() {
  const { updateResource, isLoading: isContextLoading } = useScheduler();
  const { resources: contextResources, bookings, settings, currentMode } = useScreenData('resources');
  const { getScreenMode } = useDataMode();
  const isDemo = getScreenMode('resources') === 'demo';

  // Resource Directory shows ALL resources across every company (not filtered
  // by the currently selected company).  In demo mode we fall back to the
  // context data which already contains mock resources.
  const [allResources, setAllResources] = useState<typeof contextResources>([]);
  const [allResourcesLoading, setAllResourcesLoading] = useState(false);

  useEffect(() => {
    if (isDemo) {
      setAllResources(contextResources);
      return;
    }
    let cancelled = false;
    setAllResourcesLoading(true);
    resourcesApi.getAllForOrganisation()
      .then(data => {
        if (!cancelled) setAllResources(data.map(mapApiResourceToResource));
      })
      .catch(() => {
        if (!cancelled) setAllResources(contextResources);
      })
      .finally(() => {
        if (!cancelled) setAllResourcesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isDemo, contextResources]);

  const resources = isDemo ? contextResources : allResources;
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [utilizationPeriod, setUtilizationPeriod] = useState<UtilizationPeriod>('month');
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [selectedResource, setSelectedResource] = useState<typeof resources[number] | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Check if a photoUrl is a valid, loadable image URL
  const isValidPhotoUrl = (url: string | undefined | null): boolean => {
    if (!url || url.trim() === '') return false;
    // Filter out common placeholder/invalid patterns
    if (url === 'null' || url === 'undefined') return false;
    return true;
  };

  const getStatusColor = (status: ResourceStatus) => {
    switch (status) {
      case 'available':
      case 'active':
        return 'bg-green-500';
      case 'busy':
        return 'bg-red-500';
      case 'off':
      case 'inactive':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: ResourceStatus) => {
    switch (status) {
      case 'available':
      case 'active':
        return 'active';
      case 'busy':
        return 'active';
      case 'off':
      case 'inactive':
        return 'inactive';
      default:
        return 'active';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'permanent':
        return 'Person';
      case 'contractor':
        return 'Contractor';
      case 'agency':
        return 'Asset';
      default:
        return 'Person';
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'permanent':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'contractor':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'agency':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const DAYS_ORDER: { key: keyof WorkingHoursProfile; label: string }[] = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
  ];

  const getWorkingHoursTooltip = useCallback((resource: typeof resources[number]): string => {
    const profile = (resource.useCustomWorkingProfile && resource.customWorkingProfile)
      ? resource.customWorkingProfile
      : settings.globalWorkingProfile;

    const lines = DAYS_ORDER
      .filter(({ key }) => profile[key] > 0)
      .map(({ key, label }) => `${label}: ${profile[key]}h`);

    if (lines.length === 0) return 'No working hours configured';

    const header = (resource.useCustomWorkingProfile && resource.customWorkingProfile)
      ? 'Working Hours (Custom Profile)'
      : 'Working Hours';

    return `${header}\n${lines.join('\n')}`;
  }, [settings.globalWorkingProfile]);

  // Calculate utilization based on selected period
  const calculateUtilization = (resourceId: string): number => {
    const now = new Date();
    let periodStart: Date;
    let periodEnd: Date;

    switch (utilizationPeriod) {
      case 'day':
        periodStart = startOfDay(now);
        periodEnd = endOfDay(now);
        break;
      case 'week':
        periodStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        periodEnd = endOfDay(addDays(periodStart, 6)); // Sunday
        break;
      case 'month':
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
        break;
      case 'quarter':
        periodStart = startOfQuarter(now);
        periodEnd = endOfQuarter(now);
        break;
      case 'year':
        periodStart = startOfYear(now);
        periodEnd = endOfYear(now);
        break;
      default:
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
    }

    // Find the resource to pass to the calculation function
    const resource = resources.find(r => String(r.id) === String(resourceId));
    
    return calculateResourceUtilization(
      resourceId, 
      bookings, 
      periodStart, 
      periodEnd, 
      resource, 
      settings.globalWorkingProfile
    );
  };

  // Get unique departments for filter
  const departments = useMemo(() => {
    return Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.department)
          .map((r) => r.department)
      )
    ).sort() as string[];
  }, [resources]);

  // Get unique roles for filter
  const roles = useMemo(() => {
    return Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.role)
          .map((r) => r.role as string)
      )
    ).sort();
  }, [resources]);

  // Get unique locations for filter (dynamic from current data)
  const locationOptions = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.location)
          .map((r) => r.location as string)
      )
    ).sort();
    return uniqueLocations.map((loc) => ({ value: loc, label: loc }));
  }, [resources]);

  // Filter resources
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      // Exclude the special "Unallocated" system resource from the Resources page
      if (String(resource.id) === 'unallocated') {
        return false;
      }

      // Name search
      const matchesSearch =
        searchQuery === '' ||
        resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (resource.email && resource.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (resource.skills && resource.skills.some((skill) =>
          skill.name.toLowerCase().includes(searchQuery.toLowerCase())
        ));

      // Department filter
      const matchesDepartment =
        departmentFilter === 'all' || resource.department === departmentFilter;

      // Role filter
      const matchesRole = roleFilter === 'all' || resource.role === roleFilter;

      // Resource type filter: permanent → People, contractor → Contractors, agency → Assets
      const matchesResourceType =
        resourceTypeFilter === 'all' ||
        (resourceTypeFilter === 'people' && resource.type === 'permanent') ||
        (resourceTypeFilter === 'contractors' && resource.type === 'contractor') ||
        (resourceTypeFilter === 'assets' && resource.type === 'agency');

      // Location filter (multi-select — must match any of the selected locations)
      const matchesLocation =
        locationFilter.length === 0 ||
        (resource.location != null && locationFilter.includes(resource.location));

      // Status filter: active = available/busy/active, inactive = off/inactive
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && (resource.status === 'available' || resource.status === 'busy' || resource.status === 'active')) ||
        (statusFilter === 'inactive' && (resource.status === 'off' || resource.status === 'inactive'));

      return matchesSearch && matchesDepartment && matchesRole && matchesResourceType && matchesLocation && matchesStatus;
    });
  }, [resources, searchQuery, departmentFilter, roleFilter, resourceTypeFilter, locationFilter, statusFilter]);

  // Clear all filters at once
  const clearAllFilters = () => {
    setSearchQuery('');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setResourceTypeFilter('all');
    setLocationFilter([]);
    setStatusFilter('all');
  };

  // Build active filter chips for the summary row
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (searchQuery)
      chips.push({ key: 'search', label: `Search: "${searchQuery}"`, clear: () => setSearchQuery('') });
    if (departmentFilter !== 'all')
      chips.push({ key: 'dept', label: `Dept: ${departmentFilter}`, clear: () => setDepartmentFilter('all') });
    if (roleFilter !== 'all')
      chips.push({ key: 'role', label: `Role: ${roleFilter}`, clear: () => setRoleFilter('all') });
    if (resourceTypeFilter !== 'all')
      chips.push({ key: 'type', label: `Type: ${resourceTypeFilter.charAt(0).toUpperCase() + resourceTypeFilter.slice(1)}`, clear: () => setResourceTypeFilter('all') });
    locationFilter.forEach((loc) =>
      chips.push({ key: `loc-${loc}`, label: `Location: ${loc}`, clear: () => setLocationFilter((prev) => prev.filter((l) => l !== loc)) })
    );
    if (statusFilter !== 'all')
      chips.push({ key: 'status', label: `Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`, clear: () => setStatusFilter('all') });
    return chips;
  }, [searchQuery, departmentFilter, roleFilter, resourceTypeFilter, locationFilter, statusFilter]);

  // Handle view details click
  const handleViewDetails = (resource: typeof resources[number]) => {
    setSelectedResource(resource);
    setIsDetailsModalOpen(true);
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
    <div className="w-full px-6 py-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Resource Directory</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive catalogue of all resources across your organisation
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground text-right">
            <span className="font-semibold text-foreground">{filteredResources.length}</span>
            {' of '}
            <span className="font-semibold text-foreground">{resources.length - 1}</span>
            {' resources'}
          </span>
          <DataModeToggle screen="resources" />
        </div>
      </div>

      {/* ── Filter Panel ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">

        {/* Row A — Search (primary, full-width) */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by name, email or skill..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10"
          />
        </div>

        {/* Row B — Attribute filters + view toggle */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Department */}
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[175px]">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Role */}
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[175px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role} value={role}>{role}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Location — multi-select */}
          <div className="w-[190px]">
            <MultiSelect
              options={locationOptions}
              placeholder="All Locations"
              value={locationFilter}
              onValueChange={setLocationFilter}
              emptyMessage="No locations found."
            />
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-border shrink-0" />

          {/* Resource Type — inline pill toggles */}
          <div className="flex items-center gap-1">
            {(['all', 'people', 'contractors', 'assets'] as const).map((val) => (
              <button
                key={val}
                onClick={() => setResourceTypeFilter(val)}
                className={[
                  'px-3 h-8 rounded-full text-sm font-medium border transition-colors',
                  resourceTypeFilter === val
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground',
                ].join(' ')}
              >
                {val === 'all' ? 'All Types' : val.charAt(0).toUpperCase() + val.slice(1)}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-border shrink-0" />

          {/* Status — inline pill toggles */}
          <div className="flex items-center gap-1">
            {(['all', 'active', 'inactive'] as const).map((val) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                className={[
                  'px-3 h-8 rounded-full text-sm font-medium border transition-colors',
                  statusFilter === val
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground',
                ].join(' ')}
              >
                {val === 'all' ? 'All' : val.charAt(0).toUpperCase() + val.slice(1)}
              </button>
            ))}
          </div>

          {/* View toggle — pushed to the far right */}
          <div className="ml-auto shrink-0">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value) => value && setViewMode(value as ViewMode)}
              variant="outline"
            >
              <ToggleGroupItem value="card" aria-label="Card view">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="grid" aria-label="List view">
                <LayoutList className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {/* Row C — Active filter chips (only rendered when filters are active) */}
        {activeFilters.length > 0 && <div className="flex items-center flex-wrap gap-2">
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border"
            >
              {f.label}
              <button
                onClick={f.clear}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                aria-label={`Remove ${f.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {activeFilters.length > 1 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              Clear all
            </button>
          )}
        </div>}

      </div>

      {/* Resources Card View */}
      {viewMode === 'card' && (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredResources.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No resources found
            </div>
          ) : (
            filteredResources.map((resource) => {
              const isActive = resource.status === 'available' || resource.status === 'busy' || resource.status === 'active';
              return (
                <Card
                  key={resource.id}
                  onClick={() => handleViewDetails(resource)}
                  className="overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 group"
                >
                  {/* Header Image / Initials Fallback */}
                  <div className="relative h-32">
                    {isValidPhotoUrl(resource.photoUrl) ? (
                      <>
                        <img
                          src={resource.photoUrl}
                          alt={resource.name}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                          onError={(e) => {
                            // Hide broken image, show initials fallback
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full flex items-center justify-center bg-[oklch(50%_0.0896_194.8)]" style={{ display: 'none' }}>
                          <span className="text-3xl font-semibold text-white/90 tracking-wider">
                            {getInitials(resource.name)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[oklch(50%_0.0896_194.8)]">
                        <span className="text-3xl font-semibold text-white/90 tracking-wider">
                          {getInitials(resource.name)}
                        </span>
                      </div>
                    )}
                    {/* Active / Inactive badge — top right */}
                    <div className="absolute top-2 right-2">
                      <span className={[
                        'inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold backdrop-blur-sm',
                        isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-zinc-600 text-zinc-300',
                      ].join(' ')}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <CardContent className="pt-4 pb-4 px-4">
                    <div className="flex flex-col space-y-3">
                      {/* Name only */}
                      <div>
                        <h3 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors">{resource.name}</h3>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-foreground font-medium">{resource.role}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground">{resource.department || '-'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground">{resource.location || '-'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground truncate">{resource.email || '-'}</span>
                        </div>
                      </div>

                      {/* Top Skills */}
                      {resource.skills && resource.skills.length > 0 && (
                        <div className="pt-3 border-t">
                          <div className="text-xs font-medium text-muted-foreground mb-2">Top Skills</div>
                          <div className="flex flex-wrap gap-1.5">
                            {resource.skills.slice(0, 2).map((skill, index) => (
                              <div 
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-50 text-cyan-700 text-xs font-medium"
                              >
                                <span>{typeof skill === 'string' ? skill : skill.name}</span>
                                <span className="text-cyan-600 text-[10px]">{'★'.repeat(typeof skill === 'string' ? 5 : skill.rating)}</span>
                              </div>
                            ))}
                            {resource.skills.length > 2 && (
                              <div className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-muted-foreground text-xs font-medium">
                                +{resource.skills.length - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* View Details Button */}
                      <div className="pt-3 border-t">
                        <button className="w-full text-xs font-semibold text-primary border border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-all duration-150 rounded-md py-2 flex items-center justify-center gap-1.5">
                          <Eye className="h-3.5 w-3.5" />
                          View Details
                        </button>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Resources List View */}
      {viewMode === 'grid' && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="w-[250px]">Name</TableHead>
                <TableHead className="w-[180px]">Role</TableHead>
                <TableHead className="w-[150px]">Department</TableHead>
                <TableHead className="w-[250px]">Email</TableHead>
                <TableHead className="w-[150px]">Location</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredResources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No resources found
                  </TableCell>
                </TableRow>
              ) : (
                filteredResources.map((resource) => {
                  return (
                    <TableRow
                      key={resource.id}
                      className="h-16 cursor-pointer hover:bg-accent/60 transition-colors"
                      onClick={() => handleViewDetails(resource)}
                    >
                      <TableCell className="align-middle">
                        <div className="relative">
                          <Avatar className="h-10 w-10 rounded-md">
                            {isValidPhotoUrl(resource.photoUrl) && <AvatarImage src={resource.photoUrl} alt={resource.name} />}
                            <AvatarFallback className="rounded-md bg-[oklch(50%_0.0896_194.8)] text-white font-semibold text-xs">{getInitials(resource.name)}</AvatarFallback>
                          </Avatar>
                          <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${getStatusColor(resource.status)}`} />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium align-middle">{resource.name}</TableCell>
                      <TableCell className="align-middle">{resource.role}</TableCell>
                      <TableCell className="align-middle">{resource.department || '-'}</TableCell>
                      <TableCell className="align-middle text-muted-foreground">{resource.email || '-'}</TableCell>
                      <TableCell className="align-middle">{resource.location || '-'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Resource Details Modal */}
      <ResourceDetailsModal
        resource={selectedResource}
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
      />
    </div>
      {isContextLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium">Loading Resources...</span>
          </div>
        </div>
      )}
    </div>
  );
}
